import apiSlice from "../apiSlice";
import type { ProductItemType } from "src/types/products/ProductItem";
import type { ProductDetailType, ProductListResponseType } from "src/types/products/ProductResponses";

export interface ProductsQueryArgs {
    page?: number | null;
    search?: string | null;
    startPrice?: number | null;
    endPrice?: number | null;
    haveDiscount?: boolean | null;
    orderBy?: string | null;
    orderDir?: string | null;
}

const getProductsListQuery = (filters?: ProductsQueryArgs): string => {
    const params = new URLSearchParams();

    if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                return;
            }

            params.append(key, String(value));
        });
    }

    const queryString = params.toString();
    return `/products/${queryString ? `?${queryString}` : ""}`;
};

export const productsApiSlice = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        products: build.query<ProductListResponseType, ProductsQueryArgs | void>({
            keepUnusedDataFor: 120,
            query: (filters) => getProductsListQuery(filters ?? undefined),
            providesTags: (result) => result ? [
                ...result.results.map((product) => ({ type: "Product" as const, id: product.id })),
                { type: "Product" as const, id: "LIST" },
            ] : [{ type: "Product" as const, id: "LIST" }],
        }),
        productRetrieve: build.query<ProductDetailType, string>({
            query: (slug: string) => (`/products/${slug}`),
            providesTags: (result) => result ? [
                { type: "Product" as const, id: result.id },
                ...result.related_products.map((product) => ({ type: "Product" as const, id: product.id })),
            ] : [{ type: "Product" as const, id: "DETAIL" }],
        }),
        randomProducts: build.query<ProductItemType[], void>({
            query: () => "/products/random/",
            keepUnusedDataFor: 300,
            providesTags: (result) => result ? [
                ...result.map((product) => ({ type: "Product" as const, id: product.id })),
                { type: "Product" as const, id: "RANDOM" },
            ] : [{ type: "Product" as const, id: "RANDOM" }],
        }),
        productsBySlugs: build.query<ProductItemType[], string[]>({
            keepUnusedDataFor: 60,
            query: (slugs) => (`/products/by-slugs/?slugs=${encodeURIComponent(slugs.join(","))}`),
            providesTags: (result) => result ? [
                ...result.map((product) => ({ type: "Product" as const, id: product.id })),
                { type: "Product" as const, id: "BY_SLUGS" },
            ] : [{ type: "Product" as const, id: "BY_SLUGS" }],
        }),
    })
})


export const {
    useProductsQuery,
    useProductRetrieveQuery,
    useRandomProductsQuery,
    useProductsBySlugsQuery,
} = productsApiSlice;
